/**
 * ClientIncidents - Portail Client de Déclaration & Suivi d'Incidents
 * Version 2.0 — Redesign PM + UX aligned
 * 
 * P0: Wizard multi-étapes pour déclaration facile
 * P1: Catégories obligatoires, suivi temps réel
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import {
  AlertTriangle, Search, RefreshCw, Plus, Clock,
  MessageSquare, ChevronRight, X, Send,
  CheckCircle, MapPin, Truck, ArrowLeft, ArrowRight,
  FileText, Camera, Shield, ChevronDown, ChevronUp,
  AlertCircle, Loader2, Upload
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

// ============================================================
// Design Tokens (aligned with UX guidelines)
// ============================================================

const STATUS_CONFIG = {
  nouveau: { label: 'Nouveau', cls: 'bg-slate-100 text-slate-700 border border-slate-200' },
  en_cours: { label: 'En cours de traitement', cls: 'bg-blue-50 text-blue-700 border border-blue-200' },
  en_attente_client: { label: 'En attente de votre réponse', cls: 'bg-amber-50 text-amber-700 border border-amber-200' },
  en_attente_interne: { label: 'En cours de traitement', cls: 'bg-blue-50 text-blue-700 border border-blue-200' },
  resolu: { label: 'Résolu', cls: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
  clos: { label: 'Clos', cls: 'bg-gray-100 text-gray-500 border border-gray-200 border-dashed' }
};

const SEVERITY_CONFIG = {
  critique: { label: 'Critique', cls: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500', border: 'border-l-red-500' },
  elevee: { label: 'Urgente', cls: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500', border: 'border-l-orange-500' },
  moyenne: { label: 'Moyenne', cls: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-400', border: 'border-l-amber-400' },
  faible: { label: 'Faible', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', border: 'border-l-emerald-500' }
};

const INCIDENT_TYPES = {
  retard: 'Retard de livraison',
  dommage_marchandise: 'Dommage marchandise',
  perte_marchandise: 'Perte de marchandise',
  panne: 'Panne véhicule',
  accident: 'Accident',
  probleme_douane: 'Problème douanier',
  probleme_client: 'Problème avec le service',
  vol: 'Vol',
  autre: 'Autre'
};

const URGENCY_OPTIONS = [
  { value: 'faible', label: 'Faible', desc: 'Peut attendre quelques jours', dot: 'bg-emerald-500' },
  { value: 'moyenne', label: 'Moyenne', desc: 'À traiter cette semaine', dot: 'bg-amber-400' },
  { value: 'elevee', label: 'Urgente', desc: 'À traiter rapidement', dot: 'bg-orange-500' },
  { value: 'critique', label: 'Critique', desc: 'Intervention immédiate requise', dot: 'bg-red-500' }
];

// ============================================================
// Stepper Component
// ============================================================
const Stepper = ({ steps, currentStep }) => (
  <div className="flex items-center w-full mb-8" data-testid="wizard-stepper">
    {steps.map((step, idx) => {
      const isCompleted = idx < currentStep;
      const isCurrent = idx === currentStep;
      return (
        <React.Fragment key={idx}>
          <div className="flex flex-col items-center flex-1">
            <div className={`
              w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300
              ${isCompleted ? 'bg-emerald-500 text-white' : ''}
              ${isCurrent ? 'bg-white border-2 border-emerald-500 text-emerald-600 ring-4 ring-emerald-50' : ''}
              ${!isCompleted && !isCurrent ? 'bg-white border-2 border-slate-200 text-slate-400' : ''}
            `}>
              {isCompleted ? <CheckCircle className="w-4 h-4" /> : idx + 1}
            </div>
            <span className={`mt-2 text-[11px] font-medium tracking-wide ${isCurrent ? 'text-emerald-700' : 'text-slate-400'}`}>
              {step}
            </span>
          </div>
          {idx < steps.length - 1 && (
            <div className={`h-[2px] flex-1 -mt-5 mx-1 transition-colors duration-300 ${idx < currentStep ? 'bg-emerald-500' : 'bg-slate-200'}`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ============================================================
// Incident Creation Wizard (3 steps)
// ============================================================
const IncidentWizard = ({ onClose, onSuccess, operations }) => {
  const [step, setStep] = useState(0);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    operation_id: '',
    incident_type: '',
    severity: 'moyenne',
    priority: 'normale',
    cause: '',
    description: '',
    location: ''
  });

  const steps = ['Type & Urgence', 'Détails', 'Confirmation'];

  const canNext = () => {
    if (step === 0) return form.incident_type !== '';
    if (step === 1) return form.cause.length >= 5;
    return true;
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/incidents`, {
        ...form,
        operation_id: form.operation_id || null
      }, {});
      try { (await import('../utils/analytics')).trackFeature('incident.report', { type: form.incident_type, severity: form.severity }); } catch (_) {}
      toast({ title: 'Incident créé', description: `Votre signalement ${res.data.incident_id} a été enregistré. Notre équipe va le traiter rapidement.` });
      onSuccess();
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Impossible de créer l\'incident', variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="incident-wizard-overlay">
      <Card className="w-full max-w-xl max-h-[90vh] overflow-auto shadow-xl border-0">
        <CardHeader className="pb-2 border-b bg-slate-50/80">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Barlow, sans-serif' }}>
              Signaler un incident
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="wizard-close-btn">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-6 pb-4">
          <Stepper steps={steps} currentStep={step} />

          {/* Step 0: Type & Urgence */}
          {step === 0 && (
            <div className="space-y-5" data-testid="wizard-step-0">
              {operations.length > 0 && (
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5 block">
                    Opération concernée
                  </label>
                  <Select
                    value={form.operation_id || 'none'}
                    onValueChange={(v) => setForm(f => ({ ...f, operation_id: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger data-testid="wizard-operation-select"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Incident général (aucune opération)</SelectItem>
                      {operations.map(op => (
                        <SelectItem key={op.id} value={op.id}>
                          {op.operationNumber || op.id?.slice(0, 8)} — {op.departure} → {op.arrival}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2 block">
                  Type de problème *
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(INCIDENT_TYPES).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, incident_type: key }))}
                      data-testid={`wizard-type-${key}`}
                      className={`
                        p-3 rounded-lg text-sm text-left border transition-all duration-200
                        ${form.incident_type === key
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-800 ring-1 ring-emerald-500'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700'
                        }
                      `}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2 block">
                  Niveau d'urgence
                </label>
                <div className="space-y-2">
                  {URGENCY_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, severity: opt.value }))}
                      data-testid={`wizard-severity-${opt.value}`}
                      className={`
                        w-full p-3 rounded-lg text-left border flex items-center gap-3 transition-all duration-200
                        ${form.severity === opt.value
                          ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }
                      `}
                    >
                      <span className={`w-3 h-3 rounded-full ${opt.dot} shrink-0`} />
                      <div>
                        <p className="text-sm font-medium text-slate-800">{opt.label}</p>
                        <p className="text-xs text-slate-500">{opt.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Détails */}
          {step === 1 && (
            <div className="space-y-5" data-testid="wizard-step-1">
              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5 block">
                  Décrivez le problème * <span className="normal-case font-normal">(min. 5 caractères)</span>
                </label>
                <Textarea
                  value={form.cause}
                  onChange={(e) => setForm(f => ({ ...f, cause: e.target.value }))}
                  placeholder="Ex: La marchandise est arrivée avec des dommages visibles sur les cartons..."
                  className="min-h-[100px] resize-none"
                  data-testid="wizard-cause-input"
                />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5 block">
                  Détails supplémentaires <span className="normal-case font-normal">(optionnel)</span>
                </label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Informations complémentaires, références, numéros..."
                  className="min-h-[70px] resize-none"
                  data-testid="wizard-description-input"
                />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5 block">
                  Lieu de l'incident <span className="normal-case font-normal">(optionnel)</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    value={form.location}
                    onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))}
                    placeholder="Ex: Abidjan, Port d'Abidjan, Km 15..."
                    className="pl-9"
                    data-testid="wizard-location-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Confirmation */}
          {step === 2 && (
            <div className="space-y-4" data-testid="wizard-step-2">
              <div className="p-4 rounded-xl bg-emerald-50/70 border border-emerald-200">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-emerald-800">Prêt à envoyer</p>
                    <p className="text-xs text-emerald-600 mt-0.5">
                      Votre signalement sera traité par notre équipe. Vous recevrez un numéro de suivi et pourrez suivre l'avancement en temps réel.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                <div className="p-3 flex justify-between items-center">
                  <span className="text-xs text-slate-500 font-medium">Type</span>
                  <span className="text-sm font-medium text-slate-800">{INCIDENT_TYPES[form.incident_type] || '—'}</span>
                </div>
                <div className="p-3 flex justify-between items-center">
                  <span className="text-xs text-slate-500 font-medium">Urgence</span>
                  <span className="flex items-center gap-2 text-sm font-medium text-slate-800">
                    <span className={`w-2.5 h-2.5 rounded-full ${SEVERITY_CONFIG[form.severity]?.dot}`} />
                    {SEVERITY_CONFIG[form.severity]?.label}
                  </span>
                </div>
                <div className="p-3">
                  <span className="text-xs text-slate-500 font-medium block mb-1">Description</span>
                  <p className="text-sm text-slate-700">{form.cause}</p>
                </div>
                {form.description && (
                  <div className="p-3">
                    <span className="text-xs text-slate-500 font-medium block mb-1">Détails</span>
                    <p className="text-sm text-slate-600">{form.description}</p>
                  </div>
                )}
                {form.location && (
                  <div className="p-3 flex justify-between items-center">
                    <span className="text-xs text-slate-500 font-medium">Lieu</span>
                    <span className="text-sm text-slate-700 flex items-center gap-1"><MapPin className="h-3 w-3" />{form.location}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-4 border-t">
            {step > 0 ? (
              <Button variant="ghost" onClick={() => setStep(s => s - 1)} data-testid="wizard-prev-btn">
                <ArrowLeft className="h-4 w-4 mr-1" /> Retour
              </Button>
            ) : (
              <Button variant="ghost" onClick={onClose}>Annuler</Button>
            )}

            {step < 2 ? (
              <Button
                onClick={() => setStep(s => s + 1)}
                disabled={!canNext()}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="wizard-next-btn"
              >
                Suivant <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleCreate}
                disabled={creating}
                className="bg-emerald-500 hover:bg-emerald-600 min-w-[140px]"
                data-testid="wizard-submit-btn"
              >
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Envoyer
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================
// Incident Card (Active)
// ============================================================
const ActiveIncidentCard = ({ incident, onClick }) => {
  const sevConfig = SEVERITY_CONFIG[incident.severity] || SEVERITY_CONFIG.moyenne;
  const statusConfig = STATUS_CONFIG[incident.status] || STATUS_CONFIG.nouveau;
  const hasNewResponse = incident.has_new_staff_message;

  return (
    <div
      onClick={() => onClick(incident)}
      data-testid={`incident-card-${incident.incident_id}`}
      className={`
        group relative bg-white rounded-lg border border-slate-200 overflow-hidden
        cursor-pointer hover:shadow-md transition-all duration-200
        border-l-4 ${sevConfig.border}
        ${hasNewResponse ? 'ring-2 ring-emerald-400 ring-offset-1' : ''}
      `}
    >
      {hasNewResponse && (
        <div className="absolute top-2 right-2">
          <span className="flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
          </span>
        </div>
      )}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="font-mono text-xs font-semibold text-slate-500" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {incident.incident_id}
          </span>
          <Badge className={`text-[10px] px-2 py-0 h-5 ${statusConfig.cls}`}>
            {statusConfig.label}
          </Badge>
        </div>
        <p className="text-sm font-medium text-slate-800 mb-1 line-clamp-1">
          {INCIDENT_TYPES[incident.incident_type] || incident.incident_type_label || 'Incident'}
        </p>
        <p className="text-xs text-slate-500 line-clamp-2 mb-3">{incident.cause}</p>
        <div className="flex items-center justify-between text-[11px] text-slate-400">
          <div className="flex items-center gap-3">
            {incident.operation_reference && (
              <span className="flex items-center gap-1">
                <Truck className="h-3 w-3" />
                {incident.operation_reference}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {new Date(incident.updated_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
            </span>
          </div>
          <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-emerald-500 transition-colors" />
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Incident Detail View (Client)
// ============================================================
const IncidentDetailView = ({ incident, onBack, onRefresh }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/incidents/${incident.incident_id}/messages`, {});
        setMessages(res.data.messages || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchMessages();
  }, [incident.incident_id]);

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/incidents/${incident.incident_id}/messages`,
        { content: newMessage, is_internal: false },
        {}
      );
      setNewMessage('');
      const res = await axios.get(`${API}/api/incidents/${incident.incident_id}/messages`, {});
      setMessages(res.data.messages || []);
      onRefresh();
      toast({ title: 'Message envoyé' });
    } catch (err) {
      toast({ title: 'Erreur', description: 'Impossible d\'envoyer', variant: 'destructive' });
    } finally { setSending(false); }
  };

  const sevConfig = SEVERITY_CONFIG[incident.severity] || SEVERITY_CONFIG.moyenne;
  const statusConfig = STATUS_CONFIG[incident.status] || STATUS_CONFIG.nouveau;

  return (
    <div className="h-full flex flex-col bg-white" data-testid="incident-detail-view">
      {/* Header */}
      <div className="p-4 border-b bg-slate-50/80">
        <Button variant="ghost" size="sm" onClick={onBack} className="mb-3 -ml-2 text-slate-500 hover:text-slate-800" data-testid="incident-detail-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> Retour aux incidents
        </Button>
        <div className="flex items-center gap-2 mb-2">
          <span className="font-mono text-sm font-bold text-slate-800" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {incident.incident_id}
          </span>
          <Badge className={`text-xs ${statusConfig.cls}`}>{statusConfig.label}</Badge>
          <Badge className={`text-xs ${sevConfig.cls}`}>
            <span className={`w-2 h-2 rounded-full ${sevConfig.dot} mr-1.5`} />
            {sevConfig.label}
          </Badge>
        </div>
        <p className="text-base font-semibold text-slate-800" style={{ fontFamily: 'Barlow, sans-serif' }}>
          {INCIDENT_TYPES[incident.incident_type] || incident.incident_type_label}
        </p>
        {incident.operation_reference && (
          <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
            <Truck className="h-3.5 w-3.5" /> Opération : {incident.operation_reference}
          </p>
        )}
      </div>

      {/* Details */}
      <div className="p-4 border-b bg-white">
        <div className="space-y-3">
          <div>
            <span className="text-[11px] uppercase tracking-wider font-bold text-slate-400">Description</span>
            <p className="text-sm text-slate-700 mt-0.5">{incident.cause}</p>
          </div>
          {incident.description && (
            <div>
              <span className="text-[11px] uppercase tracking-wider font-bold text-slate-400">Détails</span>
              <p className="text-sm text-slate-600 mt-0.5">{incident.description}</p>
            </div>
          )}
          {incident.location && (
            <div className="flex items-center gap-1.5 text-sm text-slate-600">
              <MapPin className="h-3.5 w-3.5 text-slate-400" />{incident.location}
            </div>
          )}
          <p className="text-xs text-slate-400">
            Créé le {new Date(incident.created_at).toLocaleString('fr-FR')}
          </p>
          {incident.resolution && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1">Résolution</p>
              <p className="text-sm text-emerald-800">{incident.resolution}</p>
            </div>
          )}
        </div>
      </div>

      {/* Conversation */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="px-4 py-2.5 border-b bg-slate-50/50">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <MessageSquare className="h-3.5 w-3.5" /> Conversation
          </h3>
        </div>
        <ScrollArea className="flex-1 p-4">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
          ) : messages.length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <MessageSquare className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Aucun message</p>
              <p className="text-xs mt-0.5">Envoyez un message pour démarrer la conversation</p>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.sender_role === 'client' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-xl px-4 py-2.5 ${
                    msg.sender_role === 'client'
                      ? 'bg-emerald-500 text-white'
                      : 'bg-slate-100 text-slate-800'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    <p className={`text-[10px] mt-1 ${msg.sender_role === 'client' ? 'text-emerald-100' : 'text-slate-400'}`}>
                      {msg.sender_role === 'client' ? 'Vous' : 'Baobab Connect'} — {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {incident.status !== 'clos' ? (
          <div className="p-3 border-t bg-white">
            <div className="flex gap-2">
              <Textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Votre message..."
                className="min-h-[50px] max-h-[100px] resize-none text-sm"
                data-testid="incident-message-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
                }}
              />
              <Button
                onClick={handleSend}
                disabled={!newMessage.trim() || sending}
                className="bg-emerald-500 hover:bg-emerald-600 self-end"
                data-testid="incident-send-btn"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-3 border-t bg-slate-50 text-center text-sm text-slate-500">
            Cet incident est clos. Créez un nouveau signalement si besoin.
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Main Component
// ============================================================
export default function ClientIncidents() {
  const { user } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const fetchIncidents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter) params.append('status', statusFilter);
      const res = await axios.get(`${API}/api/incidents?${params.toString()}`, {});
      setIncidents(res.data.incidents || []);
    } catch (err) {
      toast({ title: 'Erreur', description: 'Impossible de charger vos incidents', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [searchQuery, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/incidents/stats`, {});
      setStats(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchOperations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/transport-requests?limit=30`, {});
      setOperations(res.data.requests || res.data || []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchIncidents(); fetchStats(); fetchOperations(); }, [fetchIncidents, fetchStats, fetchOperations]);

  const handleRefresh = () => { fetchIncidents(); fetchStats(); };

  const handleIncidentClick = async (incident) => {
    try {
      const res = await axios.get(`${API}/api/incidents/${incident.incident_id}`, {});
      setSelectedIncident(res.data);
    } catch (err) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  // Separate active vs closed incidents
  const activeIncidents = incidents.filter(i => !['resolu', 'clos'].includes(i.status));
  const closedIncidents = incidents.filter(i => ['resolu', 'clos'].includes(i.status));

  if (selectedIncident) {
    return (
      <div className="h-[calc(100vh-64px)] bg-white" data-testid="client-incidents-detail">
        <IncidentDetailView
          incident={selectedIncident}
          onBack={() => { setSelectedIncident(null); handleRefresh(); }}
          onRefresh={handleRefresh}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50" data-testid="client-incidents">
      <div className="max-w-4xl mx-auto py-8 px-4 md:px-6">

        {/* Hero Action Card */}
        <div
          onClick={() => setShowWizard(true)}
          data-testid="hero-report-incident"
          className="
            group relative mb-8 p-6 rounded-xl bg-white border-2 border-dashed border-emerald-300
            hover:border-emerald-500 hover:shadow-lg cursor-pointer transition-all duration-300
          "
        >
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0 group-hover:bg-emerald-100 transition-colors">
              <AlertTriangle className="h-7 w-7 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Barlow, sans-serif' }}>
                Signaler un incident
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">
                Un problème avec votre livraison ? Décrivez-le en 3 étapes et notre équipe le prendra en charge.
              </p>
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-xs font-medium text-emerald-600 group-hover:translate-x-1 transition-transform">Démarrer</span>
              <ArrowRight className="h-4 w-4 text-emerald-500 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>

        {/* Header stats */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-lg md:text-2xl font-bold text-slate-800" style={{ fontFamily: 'Barlow, sans-serif' }}>
              Mes incidents
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {stats?.total || 0} incident(s) — {stats?.new_responses || 0} nouvelle(s) réponse(s)
            </p>
          </div>
          <Button variant="outline" size="icon" onClick={handleRefresh} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Search & Filter */}
        <div className="flex gap-2 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Rechercher un incident..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[180px]" data-testid="status-filter">
              <SelectValue placeholder="Tous les statuts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Active Incidents */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-slate-300" />
          </div>
        ) : incidents.length === 0 ? (
          <Card className="text-center py-12 border-dashed" data-testid="empty-state">
            <CardContent>
              <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold text-slate-700 mb-1" style={{ fontFamily: 'Barlow, sans-serif' }}>Aucun incident</h3>
              <p className="text-sm text-slate-500 mb-5">Vous n'avez pas encore signalé de problème.</p>
              <Button onClick={() => setShowWizard(true)} className="bg-emerald-500 hover:bg-emerald-600" data-testid="empty-state-cta">
                <Plus className="h-4 w-4 mr-1" /> Signaler un incident
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Active Section */}
            {activeIncidents.length > 0 && (
              <div className="mb-6">
                <h3 className="text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-3">
                  En cours ({activeIncidents.length})
                </h3>
                <div className="grid gap-3 sm:grid-cols-2">
                  {activeIncidents.map(inc => (
                    <ActiveIncidentCard key={inc.incident_id || inc.id} incident={inc} onClick={handleIncidentClick} />
                  ))}
                </div>
              </div>
            )}

            {/* Closed Section */}
            {closedIncidents.length > 0 && (
              <div>
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-3 hover:text-slate-600 transition-colors"
                  data-testid="toggle-history"
                >
                  {showHistory ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  Historique résolu ({closedIncidents.length})
                </button>
                {showHistory && (
                  <div className="space-y-2">
                    {closedIncidents.map(inc => {
                      const statusCfg = STATUS_CONFIG[inc.status] || STATUS_CONFIG.clos;
                      return (
                        <div
                          key={inc.incident_id || inc.id}
                          onClick={() => handleIncidentClick(inc)}
                          className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 bg-white/60 cursor-pointer hover:bg-white hover:border-slate-200 transition-all"
                          data-testid={`history-card-${inc.incident_id}`}
                        >
                          <CheckCircle className="h-4 w-4 text-emerald-400 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="font-mono text-[11px] text-slate-400 mr-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{inc.incident_id}</span>
                            <span className="text-sm text-slate-600">{inc.cause}</span>
                          </div>
                          <Badge className={`text-[10px] ${statusCfg.cls}`}>{statusCfg.label}</Badge>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Wizard Modal */}
      {showWizard && (
        <IncidentWizard
          onClose={() => setShowWizard(false)}
          onSuccess={() => { setShowWizard(false); handleRefresh(); }}
          operations={operations}
        />
      )}
    </div>
  );
}
